import React from 'react';
import type { ResultSectionConfig, ItemsPerRow, Product } from '../../utils/types';
import { TILE_SIZE_ITEMS_PER_ROW } from '../../utils/types';
import { tileGridStyle } from '../../utils/gridStyles';
import { useI18n } from '../../i18n/I18nContext';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { ProductCard } from '../ProductCard';

const ANIMATION_DELAY_STYLES = Array.from({ length: 17 }, (_, i) =>
  Object.freeze({ animationDelay: `${i * 25}ms` })
);

interface ProductResultsWidgetProps {
  config: ResultSectionConfig;
  products: Product[];
  total: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  itemsPerRow: ItemsPerRow;
  onProductClick: (product: Product) => void;
  onLoadMore: () => void;
}

export const ProductResultsWidget: React.FC<ProductResultsWidgetProps> = ({
  config,
  products,
  total,
  hasMore,
  isLoadingMore,
  itemsPerRow,
  onProductClick,
  onLoadMore,
}) => {
  const { t } = useI18n();
  const { small } = useFontSizes();

  if (products.length === 0) return null;

  const tileSize = config.tileSize ?? 'medium';
  const effectiveItemsPerRow = config.itemsPerRow ?? TILE_SIZE_ITEMS_PER_ROW[tileSize];
  const displayMode = config.displayMode ?? 'grid';
  const maxItems = config.maxItems ? products.slice(0, config.maxItems) : products;

  return (
    <section className="mb-8">
      <h3 className={`${small} font-semibold uppercase tracking-wider text-gray-500 mb-4`}>
        {config.title ?? t('section.products')}{' '}
        <span className="text-gray-400 font-normal">
          ({total > maxItems.length
            ? t('section.productCount', { shown: maxItems.length, total })
            : maxItems.length})
        </span>
      </h3>

      {displayMode === 'grid' ? (
        <div style={tileGridStyle(tileSize, effectiveItemsPerRow)}>
          {maxItems.map((product, index) => (
            <div key={product.id} className="animate-fade-in" style={ANIMATION_DELAY_STYLES[Math.min(index, 16)]}>
              <ProductCard product={product} onProductClick={onProductClick} tileSize={tileSize} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {maxItems.map((product, index) => (
            <div key={product.id} className="animate-fade-in" style={ANIMATION_DELAY_STYLES[Math.min(index, 16)]}>
              <ProductCard product={product} onProductClick={onProductClick} tileSize={tileSize} compact />
            </div>
          ))}
        </div>
      )}

      {hasMore && !config.maxItems && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className={[
              'flex items-center gap-2 px-6 py-3 text-base font-medium rounded-xl transition-all',
              isLoadingMore
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#f02e69] text-white hover:bg-[#d02759] shadow-lg hover:shadow-xl',
            ].join(' ')}
          >
            {isLoadingMore ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('search.loading')}
              </>
            ) : (
              <>
                {t('search.loadMore')}{' '}
                <span className="text-white/70">
                  ({t('search.loadMoreCount', { count: Math.max(0, total - products.length) })})
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};
